import React, { useState } from 'react';
import {
	Avatar,
	Checkbox,
	Flex,
	Form,
	Input,
	Radio,
	Select,
	Space,
	Typography,
} from 'antd';
import { ComponentTypes, getSignerColor } from '../../../common/componentUtils';
import { t } from '../../../utils';
import { CheckboxesSettings } from './CheckboxesSettings';
import { DropdownSettings } from './DropdownSettings';

const { Text } = Typography;

const textAlignOptions = ['Left', 'Center', 'Right'].map(label => ({
	label,
	value: label.toLowerCase(),
}));

const dateFormatOptions = [
	'DD/MM/YYYY',
	'MM/DD/YYYY',
	'YYYY/MM/DD',
	'MM-DD-YYYY',
	'DD-MM-YYYY',
	'YYYY-MM-DD',
	'DD.MM.YYYY',
].map(label => ({ label, value: label }));

const radioGroupOptions = [
	{
		label: 'selectOnlyOneLabel',
		value: 'OnlyOne',
	},
	{
		label: 'selectOneOrMoreLabel',
		value: 'OneOrMore',
	},
];

export const SignerFieldsEditor = ({
	componentsToEdit,
	documentTemplate,
	addCheckbox,
	removeCheckbox,
	checkboxGroupComponents,
	handleComponentUpdate,
	onComponentSelect,
}) => {
	const [form] = Form.useForm();
	const component = componentsToEdit[0];
	const [value, setValue] = useState(component?.groupRequirement);
	function getSignerLabelForComponentEditor(index: number, role: string) {
		return (
			<Flex align="center" style={{ height: '100%' }} gap="small">
				<Avatar
					size={16}
					style={{
						background: getSignerColor(index),
					}}
				/>
				<Text>{role}</Text>
			</Flex>
		);
	}
	const signersOptions = documentTemplate.roles?.map(role => ({
		label: getSignerLabelForComponentEditor(role?.sequence!, role.name),
		value: role.id, //signer1, signer2....
	}));

	const handleUpdate = (updatedAttribute: string, updatedValue: any) => {
		if (['assignedTo', 'name'].includes(updatedAttribute)) {
			if (updatedAttribute === 'assignedTo') {
				updatedValue = documentTemplate.roles?.filter(
					role => role.id === updatedValue
				)[0];
			}

			if (component.type === ComponentTypes.CheckGroupComponent) {
				const newCheckboxGroupComponents = checkboxGroupComponents.map(checkbox => {
					checkbox[updatedAttribute] = updatedValue;
					return checkbox;
				});

				handleComponentUpdate(newCheckboxGroupComponents, 'update');
			} else {
				handleComponentUpdate(
					[
						{
							...component,
							[updatedAttribute]: updatedValue,
						},
					],
					'update'
				);
			}
		} else {
			handleComponentUpdate(
				[
					{
						...component,
						[updatedAttribute]: updatedValue,
					},
				],
				'update'
			);
		}
	};

	const componentRenderers = {
		[ComponentTypes.InitialsComponent]: () => (
			<Form.Item label={t('textAlignLabel')}>
				<Select
					value={component?.textAlign}
					onChange={e => {
						handleUpdate('textAlign', e);
					}}
					options={textAlignOptions}
					aria-label={t('textAlignLabel')}
				/>
			</Form.Item>
		),
		[ComponentTypes.DateComponent]: () => (
			<>
				<Form.Item>
					<Checkbox
						onChange={() => handleUpdate('isDateSigned', !component?.isDateSigned)}
						checked={!!component?.isDateSigned}
					>
						{t('setToDateOfExecution')}
					</Checkbox>
				</Form.Item>
				<Form.Item label={t('dateFormatLabel')}>
					<Select
						value={component?.dateFormat}
						onChange={e => {
							handleUpdate('dateFormat', e);
						}}
						options={dateFormatOptions}
						aria-label={t('dateFormatLabel')}
					/>
				</Form.Item>
				<Form.Item label={t('textAlignLabel')}>
					<Select
						value={component?.textAlign}
						onChange={e => {
							handleUpdate('textAlign', e);
						}}
						options={textAlignOptions}
						aria-label={t('textAlignLabel')}
					/>
				</Form.Item>
			</>
		),
		[ComponentTypes.TextComponent]: () => (
			<>
				<Form.Item>
					<Checkbox
						onChange={() => handleUpdate('multiline', !component?.multiline)}
						checked={!!component?.multiline}
					>
						{t('multiLineLabel')}
					</Checkbox>
				</Form.Item>
				<Form.Item label={t('textAlignLabel')}>
					<Select
						value={component?.textAlign}
						onChange={e => {
							handleUpdate('textAlign', e);
						}}
						options={textAlignOptions}
						aria-label={t('textAlignLabel')}
					/>
				</Form.Item>
			</>
		),
		[ComponentTypes.SelectComponent]: () => (
			<Form.Item label={t('optionsLabel')}>
				<DropdownSettings component={component} handleUpdate={handleUpdate} />
			</Form.Item>
		),
		[ComponentTypes.AttachmentComponent]: () => (
			<Form.Item label={t('components.editor.helpTextLabel')}>
				<Input
					onChange={e => {
						handleUpdate('helpText', e.target.value);
					}}
					value={component.helpText}
					defaultValue={t('components.editor.defaultHelpText')}
					aria-label={t('components.editor.helpTextLabel')}
				/>
			</Form.Item>
		),
		[ComponentTypes.CheckGroupComponent]: () => (
			<>
				<Form.Item label={t('groupRule')}>
					<Radio.Group
						onChange={e => {
							handleUpdate('groupRequirement', e.target.value), setValue(e.target.value);
						}}
						aria-label={t('groupRule')}
						value={value}
					>
						<Space direction="vertical">
							{radioGroupOptions.map(option => (
								<Radio
									key={option.value}
									value={option.value}
									aria-label={t(option.label)}
								>
									{t(option.label)}
								</Radio>
							))}
						</Space>
					</Radio.Group>
				</Form.Item>
				<Form.Item label={t('checkboxes')}>
					<CheckboxesSettings
						checkboxes={checkboxGroupComponents}
						addCheckbox={addCheckbox}
						removeCheckbox={removeCheckbox}
						selectedComponent={component}
						onClick={checkbox => {
							onComponentSelect([checkbox]);
						}}
					/>
				</Form.Item>
			</>
		),
	};

	return (
		<Form form={form} layout="vertical" style={{ maxWidth: 280 }}>
			<Form.Item>
				<Checkbox
					onChange={() => handleUpdate('isRequired', !component?.isRequired)}
					checked={!!component?.isRequired}
				>
					{t('requiredFieldLabel')}
				</Checkbox>
			</Form.Item>
			<Form.Item label={t('assignedToLabel')} required>
				<Select
					value={component.assignedTo?.id}
					onChange={e => {
						handleUpdate('assignedTo', e);
					}}
					options={signersOptions}
					aria-label={t('assignedToLabel')}
				/>
			</Form.Item>
			<Form.Item
				label={
					component.type === ComponentTypes.CheckGroupComponent
						? t('groupNameLabel')
						: t('fieldNameLabel')
				}
				required
			>
				<Input
					placeholder="Please enter a field name"
					onChange={e => {
						handleUpdate('name', e.target.value);
					}}
					value={component.name}
					aria-label={
						component.type === ComponentTypes.CheckGroupComponent
							? t('groupNameLabel')
							: t('fieldNameLabel')
					}
				/>
			</Form.Item>
			{componentRenderers[component.type]?.(component, handleUpdate)}
		</Form>
	);
};
